import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { AdoptionPoint, Granularity } from '../../api/types';

interface Props {
  points: AdoptionPoint[];
  loading?: boolean;
  /** Bucket size — drives x-axis label format. Defaults to 'day'. */
  granularity?: Granularity;
}

/**
 * Format a bucket label for x-axis display. Input format depends on the
 * granularity that produced the points:
 *   - hour  : 'YYYY-MM-DDTHH:00:00Z' → 'MM-DD HH:00'
 *   - day   : 'YYYY-MM-DD'           → 'MM-DD'
 *   - month : 'YYYY-MM'              → 'YYYY-MM' (kept verbose for clarity)
 */
function formatBucket(v: string, granularity: Granularity): string {
  if (granularity === 'hour') {
    // 'YYYY-MM-DDTHH:00:00Z' → 'MM-DD HH:00'
    return `${v.slice(5, 10)} ${v.slice(11, 13)}:00`;
  }
  if (granularity === 'month') return v; // already 'YYYY-MM'
  return v.slice(5); // 'YYYY-MM-DD' → 'MM-DD'
}

export function AdoptionLineChart({ points, loading, granularity = 'day' }: Props): JSX.Element {
  const option: EChartsOption = {
    grid: { left: 48, right: 24, top: 32, bottom: 36 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
    },
    xAxis: {
      type: 'category',
      data: points.map((p) => p.date),
      axisLabel: {
        formatter: (v: string) => formatBucket(v, granularity),
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      name: 'Active users',
      nameLocation: 'middle',
      nameGap: 36,
    },
    series: [
      {
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2.5, color: '#6366f1' },
        itemStyle: { color: '#6366f1' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(99, 102, 241, 0.28)' },
              { offset: 1, color: 'rgba(99, 102, 241, 0.02)' },
            ],
          },
        },
        data: points.map((p) => p.activeUsers),
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 300, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
